import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  TextField,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import {
  History,
  Refresh,
  Visibility,
  FilterList,
  Clear,
  Person,
  CalendarToday,
} from '@mui/icons-material';
import { userActivityAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

interface UserActivity {
  id: number;
  user_id: number;
  activity_type: string;
  action: string;
  description: string | null;
  endpoint: string | null;
  method: string | null;
  screen: string | null;
  ip_address: string | null;
  user_agent: string | null;
  metadata: any;
  created_at: string;
  user: {
    id: number;
    name: string;
    email: string;
  };
}

interface UserActivityStats {
  total: number;
  today: number;
  this_week: number;
  this_month: number;
  by_type: Record<string, number>;
  by_action: Record<string, number>;
}

interface User {
  id: number;
  name: string;
  email: string;
}

const UserActivityLogs: React.FC = () => {
  const { user } = useAuth();
  const [logs, setLogs] = useState<UserActivity[]>([]);
  const [stats, setStats] = useState<UserActivityStats | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedLog, setSelectedLog] = useState<UserActivity | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  
  // Filters
  const [userIdFilter, setUserIdFilter] = useState<string>('');
  const [userNameFilter, setUserNameFilter] = useState<string>('');
  const [activityTypeFilter, setActivityTypeFilter] = useState<string>('');
  const [actionFilter, setActionFilter] = useState<string>('');
  const [screenFilter, setScreenFilter] = useState<string>('');
  const [dateFromFilter, setDateFromFilter] = useState<string>('');
  const [dateToFilter, setDateToFilter] = useState<string>('');

  // Check if user is admin
  const isPmpAdmin = () => {
    if (!user?.roles) return false;
    return user.roles.some(role => role.slug === 'pmp_admin');
  };

  useEffect(() => {
    fetchUserActivities();
    fetchStats();
    if (isPmpAdmin()) {
      fetchUsers();
    }
  }, [page, rowsPerPage]);

  const fetchUserActivities = async () => {
    setLoading(true);
    try {
      const params: any = {
        page: page + 1,
        per_page: rowsPerPage,
      };

      if (userIdFilter) params.user_id = userIdFilter;
      if (userNameFilter) params.user_name = userNameFilter;
      if (activityTypeFilter) params.activity_type = activityTypeFilter;
      if (actionFilter) params.action = actionFilter;
      if (screenFilter) params.screen = screenFilter;
      if (dateFromFilter) params.date_from = dateFromFilter;
      if (dateToFilter) params.date_to = dateToFilter;

      const response = await userActivityAPI.getAll(params);
      setLogs(response.data.data);
      setTotalCount(response.data.pagination.total);
    } catch (error: any) {
      console.error('Failed to fetch user activities:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const params: any = {};
      if (userIdFilter) params.user_id = userIdFilter;
      if (dateFromFilter) params.date_from = dateFromFilter;
      if (dateToFilter) params.date_to = dateToFilter;

      const response = await userActivityAPI.getStats(params);
      setStats(response.data.data);
    } catch (error: any) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await userActivityAPI.getUsers();
      setUsers(response.data.data);
    } catch (error: any) {
      console.error('Failed to fetch users:', error);
    }
  };

  const handleFilter = () => {
    setPage(0);
    fetchUserActivities();
    fetchStats();
  };

  const handleClearFilters = () => {
    setUserIdFilter('');
    setUserNameFilter('');
    setActivityTypeFilter('');
    setActionFilter('');
    setScreenFilter('');
    setDateFromFilter('');
    setDateToFilter('');
    setPage(0);
  };

  const handleViewDetails = async (log: UserActivity) => {
    try {
      const response = await userActivityAPI.getById(log.id);
      setSelectedLog(response.data.data);
      setDialogOpen(true);
    } catch (error: any) {
      console.error('Failed to fetch log details:', error);
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const getActivityTypeColor = (type: string) => {
    const colors: Record<string, 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'error'> = {
      authentication: 'primary',
      api_call: 'secondary',
      screen_view: 'success',
      case_visit: 'warning',
      project_visit: 'default',
    };
    return colors[type] || 'default';
  };

  if (!isPmpAdmin() && user?.id) {
    // Non-admin users can only view their own activities
    // The backend will filter automatically
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" component="h1">
          <History sx={{ mr: 1, verticalAlign: 'middle' }} />
          User Activity Logs
        </Typography>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={() => {
            fetchUserActivities();
            fetchStats();
          }}
        >
          Refresh
        </Button>
      </Box>

      {/* Stats Cards */}
      {stats && (
        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
          <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Total Activities
                </Typography>
                <Typography variant="h5">{stats.total}</Typography>
              </CardContent>
            </Card>
          </Box>
          <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Today
                </Typography>
                <Typography variant="h5">{stats.today}</Typography>
              </CardContent>
            </Card>
          </Box>
          <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  This Week
                </Typography>
                <Typography variant="h5">{stats.this_week}</Typography>
              </CardContent>
            </Card>
          </Box>
          <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  This Month
                </Typography>
                <Typography variant="h5">{stats.this_month}</Typography>
              </CardContent>
            </Card>
          </Box>
        </Box>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <FilterList sx={{ mr: 1 }} />
          <Typography variant="h6">Filters</Typography>
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          {isPmpAdmin() && (
            <>
              <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
                <FormControl fullWidth>
                  <InputLabel>User</InputLabel>
                  <Select
                    value={userIdFilter}
                    label="User"
                    onChange={(e) => setUserIdFilter(e.target.value)}
                  >
                    <MenuItem value="">All Users</MenuItem>
                    {users.map((u) => (
                      <MenuItem key={u.id} value={u.id.toString()}>
                        {u.name} ({u.email})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
                <TextField
                  fullWidth
                  label="Search by Name/Email"
                  value={userNameFilter}
                  onChange={(e) => setUserNameFilter(e.target.value)}
                  placeholder="Enter name or email"
                />
              </Box>
            </>
          )}
          <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
            <FormControl fullWidth>
              <InputLabel>Activity Type</InputLabel>
              <Select
                value={activityTypeFilter}
                label="Activity Type"
                onChange={(e) => setActivityTypeFilter(e.target.value)}
              >
                <MenuItem value="">All Types</MenuItem>
                <MenuItem value="authentication">Authentication</MenuItem>
                <MenuItem value="api_call">API Call</MenuItem>
                <MenuItem value="screen_view">Screen View</MenuItem>
                <MenuItem value="case_visit">Case Visit</MenuItem>
                <MenuItem value="project_visit">Project Visit</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
            <TextField
              fullWidth
              label="Action"
              value={actionFilter}
              onChange={(e) => setActionFilter(e.target.value)}
              placeholder="e.g., login, view_case"
            />
          </Box>
          <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
            <TextField
              fullWidth
              label="Screen"
              value={screenFilter}
              onChange={(e) => setScreenFilter(e.target.value)}
              placeholder="e.g., Cases, Projects"
            />
          </Box>
          <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
            <TextField
              fullWidth
              label="Date From"
              type="date"
              value={dateFromFilter}
              onChange={(e) => setDateFromFilter(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Box>
          <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
            <TextField
              fullWidth
              label="Date To"
              type="date"
              value={dateToFilter}
              onChange={(e) => setDateToFilter(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Box>
          <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button variant="contained" onClick={handleFilter} startIcon={<FilterList />}>
                Apply Filters
              </Button>
              <Button variant="outlined" onClick={handleClearFilters} startIcon={<Clear />}>
                Clear
              </Button>
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* Activity Logs Table */}
      <TableContainer component={Paper}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : logs.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="textSecondary">No activity logs found</Typography>
          </Box>
        ) : (
          <>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Timestamp</TableCell>
                  {isPmpAdmin() && <TableCell>User</TableCell>}
                  <TableCell>Type</TableCell>
                  <TableCell>Action</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Screen/Endpoint</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {logs.map((log) => (
                  <TableRow key={log.id} hover>
                    <TableCell>
                      <Tooltip title={formatDate(log.created_at)}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CalendarToday fontSize="small" color="action" />
                          {formatDate(log.created_at)}
                        </Box>
                      </Tooltip>
                    </TableCell>
                    {isPmpAdmin() && (
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Person fontSize="small" color="action" />
                          <Box>
                            <Typography variant="body2">{log.user.name}</Typography>
                            <Typography variant="caption" color="textSecondary">
                              {log.user.email}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                    )}
                    <TableCell>
                      <Chip
                        label={log.activity_type}
                        color={getActivityTypeColor(log.activity_type)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{log.action}</TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 300 }}>
                        {log.description || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {log.screen ? (
                        <Chip label={log.screen} size="small" variant="outlined" />
                      ) : log.endpoint ? (
                        <Typography variant="caption" noWrap sx={{ maxWidth: 200 }}>
                          {log.method} {log.endpoint}
                        </Typography>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell>
                      <Tooltip title="View Details">
                        <IconButton size="small" onClick={() => handleViewDetails(log)}>
                          <Visibility fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[25, 50, 100]}
            />
          </>
        )}
      </TableContainer>

      {/* Details Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Activity Details</DialogTitle>
        <DialogContent>
          {selectedLog && (
            <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ flex: '1 1 300px', minWidth: '200px' }}>
                  <Typography variant="subtitle2" color="textSecondary">
                    User
                  </Typography>
                  <Typography variant="body1">
                    {selectedLog.user.name} ({selectedLog.user.email})
                  </Typography>
                </Box>
                <Box sx={{ flex: '1 1 300px', minWidth: '200px' }}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Timestamp
                  </Typography>
                  <Typography variant="body1">{formatDate(selectedLog.created_at)}</Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ flex: '1 1 300px', minWidth: '200px' }}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Activity Type
                  </Typography>
                  <Chip
                    label={selectedLog.activity_type}
                    color={getActivityTypeColor(selectedLog.activity_type)}
                    size="small"
                  />
                </Box>
                <Box sx={{ flex: '1 1 300px', minWidth: '200px' }}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Action
                  </Typography>
                  <Typography variant="body1">{selectedLog.action}</Typography>
                </Box>
              </Box>
              {selectedLog.description && (
                <Box>
                  <Typography variant="subtitle2" color="textSecondary">
                    Description
                  </Typography>
                  <Typography variant="body1">{selectedLog.description}</Typography>
                </Box>
              )}
              {(selectedLog.screen || selectedLog.endpoint) && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                  {selectedLog.screen && (
                    <Box sx={{ flex: '1 1 300px', minWidth: '200px' }}>
                      <Typography variant="subtitle2" color="textSecondary">
                        Screen
                      </Typography>
                      <Typography variant="body1">{selectedLog.screen}</Typography>
                    </Box>
                  )}
                  {selectedLog.endpoint && (
                    <Box sx={{ flex: '1 1 300px', minWidth: '200px' }}>
                      <Typography variant="subtitle2" color="textSecondary">
                        Endpoint
                      </Typography>
                      <Typography variant="body1">
                        {selectedLog.method} {selectedLog.endpoint}
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}
              {(selectedLog.ip_address || selectedLog.user_agent) && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                  {selectedLog.ip_address && (
                    <Box sx={{ flex: '1 1 300px', minWidth: '200px' }}>
                      <Typography variant="subtitle2" color="textSecondary">
                        IP Address
                      </Typography>
                      <Typography variant="body1">{selectedLog.ip_address}</Typography>
                    </Box>
                  )}
                  {selectedLog.user_agent && (
                    <Box sx={{ flex: '1 1 300px', minWidth: '200px' }}>
                      <Typography variant="subtitle2" color="textSecondary">
                        User Agent
                      </Typography>
                      <Typography variant="body1" sx={{ wordBreak: 'break-word' }}>
                        {selectedLog.user_agent}
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}
              {selectedLog.metadata && Object.keys(selectedLog.metadata).length > 0 && (
                <Box>
                  <Typography variant="subtitle2" color="textSecondary">
                    Metadata
                  </Typography>
                  <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                    <pre style={{ margin: 0, fontSize: '0.875rem' }}>
                      {JSON.stringify(selectedLog.metadata, null, 2)}
                    </pre>
                  </Paper>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default UserActivityLogs;

